import { ChemistryLitElement } from '@adornis/chemistry/chemistry-tailwind-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import type { Course } from '@chancenwerk/shared/courses/Course.js';
import { CourseLernfoerderung } from '@chancenwerk/shared/courses/CourseLernfoerderung.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { map } from 'rxjs';
import type { KPF } from '../../../../src/public-access/api/KPFPublicInformationAPI.js';
import { BoardRoutes } from '../routes.js';
import './board-component-course-startdate.js';
import './board-component-flat-loader.js';

/**
 * @customElement board-component-sch-overview-grid
 */
@customElement('board-component-sch-overview-grid')
export class BoardComponentLkkOverviewGrid extends ChemistryLitElement {
  @property({ attribute: false }) public kpf: KPF | undefined = undefined;
  @property({ attribute: false }) public courses = new RXController(this, [] as Course[]);
  @property({ attribute: false }) public loading = false;
  @property({ attribute: false }) public hide: string[] = [];
  @property() public name = '';
  @property() public class = '';

  @property({ type: Boolean }) public noToggle = false;
  @property({ type: Boolean }) public noHeader = false;

  // Is set if location time or day is missing
  @property({ attribute: false }) public hasMissingData = false;

  @state() private readonly _courseMap = new RXController(
    this,
    this.courses.observable.pipe(
      map(courses => {
        const courseMap = new Map<string, Course[]>();

        for (const course of courses ?? []) {
          const key = course.weekday ?? '';
          const entry = courseMap.get(key);
          if (!entry) {
            courseMap.set(key, [course]);
          } else {
            courseMap.set(key, [...entry, course]);
          }
        }
        return courseMap;
      }),
    ),
  );

  override render() {
    const content = this.loading || !this.kpf ? this._loader() : this._content();

    if (this.noToggle) {
      return content;
    }

    return html`
      <x-toggle-content
        .label=${html`<cr-h4>${this.name}</cr-h4> ${this.hasMissingData
            ? html`<cr-icon ${css({ color: this.colors.tone.error, fontSize: '1.8rem' })}>
                report_problem
                <cr-tooltip> <cr-text> In dieser ${this.name} fehlen Daten! </cr-text> </cr-tooltip>
              </cr-icon>`
            : nothing}`}
      >
        ${content}
      </x-toggle-content>
    `;
  }

  private _loader() {
    return html` <cr-flex w100 center crossaxis-center> <cr-loader></cr-loader> </cr-flex> `;
  }

  private _content() {
    return html`
      <cr-flex horizontal space="md" space-between crossaxis-center ${css({ overflow: 'auto' })}>
        <cr-flex space="md" center>
          ${this.noHeader
            ? nothing
            : html`<cr-grid columns=${`150px 100px 120px ${'1fr '.repeat(4 - this.hide.length)}`} space="md">
                <cr-text bold>Raum</cr-text>
                <cr-text bold>Tag</cr-text>
                <cr-text bold>Zeit</cr-text>
                ${!this.hide.includes('SchuKo') ? html`<cr-text bold>SchuKo</cr-text>` : nothing}
                ${!this.hide.includes('LeKo') ? html`<cr-text bold>LeKo</cr-text>` : nothing}
                ${!this.hide.includes('Jugendlich') ? html`<cr-text bold>Jugendliche</cr-text>` : nothing}
                ${!this.hide.includes('Kinder') ? html`<cr-text bold>Kinder</cr-text>` : nothing}
              </cr-grid>`}
          ${this.courses.value.length
            ? html`
                ${repeat(
                  this._courseMap.value?.keys() ?? [],
                  l => l,
                  (location, locationIndex) => this._location(location, locationIndex),
                )}
              `
            : this.class === CourseLernfoerderung._class
              ? html`
                  <cr-flex center crossaxis-center horizontal space="sm">
                    <cr-text> Es sind keine Lernförderungskurse für dieses KPF eingetragen. </cr-text>
                  </cr-flex>
                `
              : nothing}
        </cr-flex>

        ${this.class
          ? html`<cr-flex center crossaxis center>
              <cr-button
                mode="fab"
                icon="chevron_right"
                href=${`${BoardRoutes.details}/${this.class}/${this.kpf?._id}`}
              >
                <cr-tooltip> <cr-text> Details </cr-text> </cr-tooltip>
              </cr-button>
            </cr-flex>`
          : nothing}
      </cr-flex>
    `;
  }

  private _location(location: string, locationIndex: number) {
    const firstCourse = this._courseMap.value?.get(location)?.at(0);
    return html`
      <cr-flex space="md">
        ${(firstCourse?.plannedStartDate ?? firstCourse?.startDate)
          ? html`<board-component-course-startdate
              .plannedStartdate=${firstCourse.plannedStartDate}
              .actualStartdate=${firstCourse.startDate}
            ></board-component-course-startdate>`
          : nothing}
        <cr-grid columns=${`150px 100px 120px ${'1fr '.repeat(4 - this.hide.length)}`} space="md">
          <cr-flex
            center
            crossaxis
            center
            ${css({ gridRow: `1 / span ${this._courseMap.value?.get(location)?.length}` })}
          >
            ${location !== ''
              ? location.startsWith('http')
                ? html`<cr-link href=${location}
                    ><cr-flex horizontal crossaxis-center
                      ><cr-icon ${css({ marginRight: '10px' })}>computer</cr-icon>
                      <cr-text>digitaler Kurs</cr-text></cr-flex
                    ></cr-link
                  >`
                : html` <cr-text bold> ${location} </cr-text> `
              : html`
                  <cr-flex horizontal space="xs">
                    <cr-text bold ${css({ color: this.colors.tone.error })}> Raum </cr-text>
                    <cr-text bold ${css({ color: this.colors.tone.error, fontSize: '1.4rem' })}> ? </cr-text>
                  </cr-flex>
                `}
          </cr-flex>
          ${repeat(
            this._courseMap.value?.get(location) ?? [],
            course => course._id,
            course => this._course(course),
          )}
        </cr-grid>
      </cr-flex>
      ${locationIndex + 1 === Array.from(this._courseMap.value?.keys() ?? []).length
        ? nothing
        : html` <cr-flex w100 ${css({ height: '1px', background: this.colors.tone.subtle })}></cr-flex> `}
    `;
  }

  private _course(course: Course) {
    const produkt = this.kpf?.produkt;
    // if (!(produkt instanceof ProductSCH)) throw new Error('only use with Chancenschule Produkt');

    const schuKoPercentage = this._getPercentage(course.schuKoIDs?.length ?? 0, 1);
    const leKoPercentage = this._getPercentage(course.leKoIDs?.length ?? 0, 1);
    const usStudentsPercentage = this._getPercentage(course.usStudentIDs.length, 12);
    const osStudentsPercentage = this._getPercentage(course.osStudentIDs.length, 3);

    return html`
      <cr-flex center>
        ${course.weekday
          ? html` <cr-text> ${course.weekday} </cr-text> `
          : html`
              <cr-flex horizontal space="xs">
                <cr-text ${css({ color: this.colors.tone.error })}> Tag </cr-text>
                <cr-text bold ${css({ color: this.colors.tone.error, fontSize: '1.4rem' })}> ? </cr-text>
              </cr-flex>
            `}
      </cr-flex>
      <cr-flex center>
        ${course.startTime && course.endTime
          ? html` <cr-text> ${course.startTime.toFormat('HH:mm')} - ${course.endTime.toFormat('HH:mm')} </cr-text> `
          : html`
              <cr-flex horizontal space="xs">
                <cr-text ${css({ color: this.colors.tone.error })}> Uhrzeit </cr-text>
                <cr-text bold ${css({ color: this.colors.tone.error, fontSize: '1.4rem' })}> ? </cr-text>
              </cr-flex>
            `}
      </cr-flex>

      ${this._percentageBar(!this.hide.includes('SchuKo'), schuKoPercentage, course.schuKoIDs?.length ?? 0, 1)}
      ${this._percentageBar(!this.hide.includes('LeKo'), leKoPercentage, course.leKoIDs?.length ?? 0, 1)}
      ${this._percentageBar(!this.hide.includes('Kinder'), usStudentsPercentage, course.usStudentIDs.length, 12)}
      ${this._percentageBar(!this.hide.includes('Jugendlich'), osStudentsPercentage, course.osStudentIDs.length, 3)}
    `;
  }

  private _percentageBar(visibile: boolean, value: number, count: number, all: number | undefined | null) {
    if (!visibile) {
      return nothing;
    }
    return html`<cr-flex ${css({ alignItems: 'flex-start' })}>
      <cr-flex space="sm">
        <cr-progress-bar
          ${css({ maxWidth: '180px' })}
          .value=${value}
          tone=${this._getProgressBarTone(value)}
        ></cr-progress-bar>
        <cr-text> ${count} / ${all ?? '?'} </cr-text>
      </cr-flex>
    </cr-flex>`;
  }

  private _getPercentage(actualNumber: number, neededNumber: number) {
    if (!actualNumber || !neededNumber) return 0;
    return actualNumber / neededNumber;
  }

  private _getProgressBarTone(percentage: number) {
    if (percentage <= 0.2) return 'error';
    if (percentage < 1.0) return 'warning';
    return 'success';
  }
}
